import React, { createContext, useState, useEffect, useContext } from 'react';
import authApi from '../api/auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // ตรวจสอบ token ว่ายังใช้งานได้หรือไม่
  const checkTokenValidity = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }

      // ทดสอบ token โดยเรียก API profile
      const response = await authApi.getProfile();
      if (response.success) {
        setUser(response.user);
        setToken(token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token validation failed:', error.message);
      // ถ้า token ไม่ถูกต้อง ให้ลบออก
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);
      return false;
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        // 1. โหลด user จาก localStorage ถ้ามี (เช่นหลัง social login)
        const localToken = localStorage.getItem('token');
        const localUser = localStorage.getItem('user');
        if (localToken && localUser) {
          setToken(localToken);
          setUser(JSON.parse(localUser));
          setLoading(false);
          // ไม่ต้องเช็ค token validity ซ้ำถ้าเพิ่ง login social
          return;
        }
        // 2. ถ้าไม่มี user ใน localStorage ให้เช็ค token validity ตามปกติ
        const isValid = await checkTokenValidity();
        if (!isValid) {
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        console.error("Failed to load user:", error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const login = async (username, password) => {
    setLoading(true);
    try {
      const response = await authApi.login(username, password);
      console.log("API Response for login:", response);
      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        console.log("Login successful, user set:", response.user);
        return { success: true };
      } else {
        console.error("Login API returned success: false", response.message);
        return { success: false, message: response.message || 'Unknown login error.' };
      }
    } catch (error) {
      console.error("Login failed (caught error):", error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (username, email, password) => {
    setLoading(true);
    try {
      const response = await authApi.register(username, email, password);
      
      // ถ้า register สำเร็จ และมี token และ user กลับมา ให้อัปเดต state
      if (response.success && response.token && response.user) {
        setUser(response.user);
        setToken(response.token);
      }
      
      return response;
    } catch (error) {
      console.error("Registration failed:", error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
    setToken(null);
  };

  const updateProfile = async (newProfileData) => {
    setLoading(true);
    try {
      const response = await authApi.updateProfile(newProfileData);
      if (response.success) {
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
        return { success: true, user: response.user, message: response.message };
      }
      return response;
    } catch (error) {
      console.error("Profile update failed:", error.message);
      // ถ้า token หมดอายุ ให้ logout
      if (error.message.includes('Token is not valid') || error.message.includes('No token')) {
        logout();
      }
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    try {
      const response = await authApi.updatePassword({ currentPassword, newPassword });
      return response;
    } catch (error) {
      console.error("Password update failed:", error.message);
      // ถ้า token หมดอายุ ให้ logout
      if (error.message.includes('Token is not valid') || error.message.includes('No token')) {
        logout();
      }
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (passwordData) => {
    setLoading(true);
    try {
      const response = await authApi.updatePassword(passwordData);
      return response;
    } catch (error) {
      console.error("Password update failed:", error.message);
      // ถ้า token หมดอายุ ให้ logout
      if (error.message.includes('Token is not valid') || error.message.includes('No token')) {
        logout();
      }
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      isAuthenticated: !!user, 
      loading, 
      login, 
      register, 
      logout, 
      updateProfile, 
      changePassword,
      updatePassword 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };